import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  HandshakeOutlined,
  SearchOutlined,
  CloseOutlined,
} from "@mui/icons-material";
import { dt } from "../../../shared/themes/redesignTokens";
import { PartnerCompany } from "./VisibilitySelector";

// ═══════════════════════════════════════════════════════════════
//  Tier config (mirrors VisibilitySelector)
// ═══════════════════════════════════════════════════════════════

const tierLabelKeys: Record<number, string> = {
  1: "partnerships:tier.connected",
  2: "partnerships:tier.preferred",
  3: "partnerships:tier.contracted",
};

const tierColors: Record<number, { bg: string; text: string; border: string }> = {
  1: { bg: "#EBF5FB", text: "var(--accent, #3498DB)", border: "var(--accent, #3498DB)" },
  2: { bg: "#FEF9E7", text: "#F39C12", border: "#F39C12" },
  3: { bg: "#E8F8F5", text: "#1ABC9C", border: "#1ABC9C" },
};

// Sort order: Contracted (3) first, then Preferred (2), then Connected (1)
const tierSortOrder: Record<number, number> = { 3: 0, 2: 1, 1: 2 };

// ═══════════════════════════════════════════════════════════════
//  Props
// ═══════════════════════════════════════════════════════════════

interface SelectPartnersDialogProps {
  open: boolean;
  onClose: () => void;
  partners: PartnerCompany[];
  selectedIds: string[];
  onConfirm: (ids: string[]) => void;
  translate: (key: string, options?: any) => any;
}

// ═══════════════════════════════════════════════════════════════
//  Component
// ═══════════════════════════════════════════════════════════════

const SelectPartnersDialog: React.FC<SelectPartnersDialogProps> = ({
  open,
  onClose,
  partners,
  selectedIds,
  onConfirm,
  translate,
}) => {
  // Local selection state (committed only on confirm)
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>(selectedIds);
  const [searchQuery, setSearchQuery] = useState("");

  // Reset local state when dialog opens
  React.useEffect(() => {
    if (open) {
      setLocalSelectedIds(selectedIds);
      setSearchQuery("");
    }
  }, [open, selectedIds]);

  // Sorted partners: Contracted first, then Preferred, then Connected
  const sortedPartners = useMemo(() => {
    return [...partners].sort(
      (a, b) =>
        (tierSortOrder[a.tierCode] ?? 99) - (tierSortOrder[b.tierCode] ?? 99)
    );
  }, [partners]);

  // Filtered by search
  const filteredPartners = useMemo(() => {
    if (!searchQuery.trim()) return sortedPartners;
    const q = searchQuery.toLowerCase().trim();
    return sortedPartners.filter((p) => p.name?.toLowerCase().includes(q));
  }, [sortedPartners, searchQuery]);

  // Select all / deselect all logic
  const allFilteredSelected =
    filteredPartners.length > 0 &&
    filteredPartners.every((p) => localSelectedIds.includes(p.customerId));

  const someFilteredSelected =
    filteredPartners.some((p) => localSelectedIds.includes(p.customerId)) &&
    !allFilteredSelected;

  const handleTogglePartner = (customerId: string) => {
    setLocalSelectedIds((prev) =>
      prev.includes(customerId)
        ? prev.filter((id) => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleToggleAll = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filteredPartners.map((p) => p.customerId));
      setLocalSelectedIds((prev) => prev.filter((id) => !filteredIds.has(id)));
    } else {
      const existingSet = new Set(localSelectedIds);
      const newIds = filteredPartners
        .map((p) => p.customerId)
        .filter((id) => !existingSet.has(id));
      setLocalSelectedIds((prev) => [...prev, ...newIds]);
    }
  };

  const handleConfirm = () => {
    onConfirm(localSelectedIds);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          fontFamily: dt.font,
          overflow: "hidden",
        },
      }}
    >
      {/* ── Title ─────────────────────────────── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, #27AE60 0%, #219653 100%)",
              flexShrink: 0,
            }}
          >
            <HandshakeOutlined sx={{ fontSize: "20px", color: "#fff" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: dt.font,
                fontWeight: 700,
                fontSize: "17px",
                color: dt.textPrimary,
              }}
            >
              {translate("servicing:visibility.dialogTitle")}
            </Typography>
            <Typography
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                color: dt.textSecondary,
                mt: 0.25,
              }}
            >
              {translate("servicing:visibility.dialogSubtitle")}
            </Typography>
          </Box>
        </Box>
        <Box
          onClick={onClose}
          sx={{
            cursor: "pointer",
            color: dt.textMuted,
            "&:hover": { color: dt.textPrimary },
            transition: "color 0.2s",
          }}
        >
          <CloseOutlined sx={{ fontSize: "20px" }} />
        </Box>
      </DialogTitle>

      {/* ── Content ───────────────────────────── */}
      <DialogContent sx={{ pt: "12px !important", pb: 1 }}>
        {/* Search bar (only if > 3 partners) */}
        {partners.length > 3 && (
          <TextField
            size="small"
            fullWidth
            placeholder={translate("servicing:visibility.searchPartners")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: "18px", color: dt.textMuted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.5,
              "& .MuiOutlinedInput-root": {
                fontFamily: dt.font,
                fontSize: "13px",
                borderRadius: dt.radiusSm,
                backgroundColor: dt.inputBg,
                height: "40px",
                "& fieldset": { borderColor: "transparent" },
                "&:hover fieldset": { borderColor: dt.cardBorder },
                "&.Mui-focused fieldset": {
                  borderColor: "#27AE60",
                  borderWidth: "1.5px",
                },
              },
            }}
          />
        )}

        {/* Select All / Deselect All row */}
        <Box
          onClick={handleToggleAll}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: "6px 8px",
            mb: 0.5,
            borderRadius: dt.radiusSm,
            cursor: "pointer",
            transition: "background 0.15s ease",
            "&:hover": {
              backgroundColor: "rgba(39,174,96,0.04)",
            },
          }}
        >
          <Checkbox
            checked={allFilteredSelected}
            indeterminate={someFilteredSelected}
            size="small"
            sx={{
              p: 0,
              color: dt.textMuted,
              "&.Mui-checked": { color: "#27AE60" },
              "&.MuiCheckbox-indeterminate": { color: "#27AE60" },
            }}
          />
          <Typography
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 600,
              color: dt.textSecondary,
            }}
          >
            {allFilteredSelected
              ? translate("servicing:visibility.deselectAll")
              : translate("servicing:visibility.selectAll")}
          </Typography>
          {localSelectedIds.length > 0 && (
            <Typography
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "11px",
                fontWeight: 600,
                color: "#27AE60",
                backgroundColor: "rgba(39,174,96,0.08)",
                borderRadius: dt.radiusPill,
                px: 1,
                py: "1px",
                ml: "auto",
              }}
            >
              {translate("servicing:visibility.partnersSelected", {
                count: localSelectedIds.length,
              })}
            </Typography>
          )}
        </Box>

        {/* Scrollable partner list */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            maxHeight: "340px",
            overflowY: "auto",
            overflowX: "hidden",
            borderRadius: dt.radiusSm,
            border: `1px solid ${dt.cardBorder}`,
            backgroundColor: dt.cardBg,
            p: 0.5,
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#D1D5DB",
              borderRadius: "3px",
              "&:hover": { backgroundColor: "#9CA3AF" },
            },
          }}
        >
          {filteredPartners.length === 0 ? (
            <Typography
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                color: dt.textMuted,
                textAlign: "center",
                py: 3,
              }}
            >
              {translate("servicing:visibility.searchPartners")}
            </Typography>
          ) : (
            filteredPartners.map((partner) => {
              const tc = tierColors[partner.tierCode];
              const isSelected = localSelectedIds.includes(partner.customerId);
              return (
                <Box
                  key={partner.customerId}
                  onClick={() => handleTogglePartner(partner.customerId)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: dt.radiusSm,
                    border: `1.5px solid ${isSelected ? "#27AE60" : "transparent"}`,
                    backgroundColor: isSelected
                      ? "rgba(39,174,96,0.04)"
                      : "transparent",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? "rgba(39,174,96,0.06)"
                        : "#FAFAFA",
                    },
                  }}
                >
                  {/* Checkbox */}
                  <Checkbox
                    checked={isSelected}
                    size="small"
                    sx={{
                      p: 0,
                      color: dt.textMuted,
                      "&.Mui-checked": { color: "#27AE60" },
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleTogglePartner(partner.customerId)}
                  />

                  {/* Partner avatar */}
                  {partner.logoUrl ? (
                    <Box
                      component="img"
                      src={partner.logoUrl}
                      alt={partner.name}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        backgroundColor: "#EBF5FB",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        fontFamily: dt.font,
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "var(--accent, #3498DB)",
                      }}
                    >
                      {partner.name?.charAt(0)?.toUpperCase() || "?"}
                    </Box>
                  )}

                  {/* Partner name */}
                  <Typography
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "14px",
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? "#27AE60" : dt.textPrimary,
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    {partner.name}
                  </Typography>

                  {/* Tier badge */}
                  {tc && (
                    <Box
                      sx={{
                        fontFamily: dt.font,
                        fontSize: "11px",
                        fontWeight: 600,
                        color: tc.text,
                        backgroundColor: tc.bg,
                        border: `1px solid ${tc.border}`,
                        borderRadius: dt.radiusPill,
                        padding: "2px 10px",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {tierLabelKeys[partner.tierCode] ? translate(tierLabelKeys[partner.tierCode]) : null}
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>

      {/* ── Actions ───────────────────────────── */}
      <DialogActions sx={{ padding: "12px 24px 20px", gap: "8px" }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontFamily: dt.font,
            fontWeight: 600,
            fontSize: "13px",
            color: dt.textSecondary,
            borderRadius: "8px",
          }}
        >
          {translate("common:buttons.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={localSelectedIds.length === 0}
          variant="contained"
          sx={{
            textTransform: "none",
            fontFamily: dt.font,
            fontWeight: 600,
            fontSize: "13px",
            borderRadius: "8px",
            background: "linear-gradient(135deg, #27AE60, #219653)",
            boxShadow: "0 2px 8px rgba(39,174,96,0.25)",
            "&:hover": {
              background: "linear-gradient(135deg, #219653, #27AE60)",
              boxShadow: "0 4px 12px rgba(39,174,96,0.3)",
            },
            "&.Mui-disabled": {
              background: "#D1D5DB",
              color: "#9CA3AF",
            },
          }}
        >
          {translate("servicing:visibility.confirmSelection", {
            count: localSelectedIds.length,
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectPartnersDialog;
